import React, { useState } from 'react';
import {
  LayoutDashboard, CreditCard, Users, Building2, Receipt, Upload, GitCompareArrows,
  TrendingUp, AlertTriangle, ChevronDown, ChevronRight, Menu, X, Settings, Database,
  FileText, UserCircle, Landmark, DollarSign, CalendarCheck, Percent, Shield, LogOut,
  UserCog, PieChart, LifeBuoy, Handshake, Search, ClipboardCheck, BookOpen
, Lock } from 'lucide-react';
import { colors } from '../utils/theme';
import useIsMobile from '../hooks/useIsMobile';
import { estaEmVerComo, sairDoVerComo } from '../utils/verComo';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  {
    id: 'financeiro', label: 'Financeiro', icon: CreditCard, children: [
      {
        id: 'cadastro', label: 'Cadastro', children: [
          { id: 'titulares', label: 'Titulares', icon: Users },
          { id: 'cartoes', label: 'Cartões', icon: CreditCard },
          { id: 'cofre', label: 'Cofre de cartões', icon: Lock },
          { id: 'bancos', label: 'Bancos', icon: Building2 },
        ]
      },
      {
        id: 'movimentacao', label: 'Movimentação', children: [
          { id: 'transacoes', label: 'Lançamentos', icon: Receipt },
          { id: 'importacao', label: 'Importação / Sync', icon: Upload },
        ]
      },
      {
        id: 'conciliacoes', label: 'Conciliação', icon: GitCompareArrows, children: [
          { id: 'conciliacao', label: 'Faturas de Cartão', icon: CreditCard },
          { id: 'fechamento-fatura', label: 'Fechamento de Fatura', icon: ClipboardCheck },
          { id: 'conciliacao-cc', label: 'Conta Corrente', icon: Landmark },
        ]
      },
      { id: 'consulta', label: 'Consulta', icon: Search },
      { id: 'fluxo', label: 'Fluxo de Caixa', icon: TrendingUp },
      { id: 'agenda-vencimentos', label: 'Vencimentos por Dia', icon: CalendarCheck },
      { id: 'contas-a-pagar', label: 'Contas a Pagar', icon: DollarSign },
      { id: 'centros-custo', label: 'Centro de Custos', icon: PieChart },
      { id: 'plano-contas', label: 'Plano de Contas', icon: BookOpen },
      { id: 'remuneracao', label: 'Remuneração de Investidor', icon: Percent },
    ]
  },
  { id: 'painel-investidor', label: 'Painel do Investidor', icon: UserCircle },
  // Do investidor pra ele: saldo utilizado, memória de cálculo e histórico de
  // pagamentos — os três itens que a Cláusula 10 do contrato promete.
  { id: 'meu-painel', label: 'Meu painel', icon: TrendingUp },
  { id: 'meu-cadastro', label: 'Meu cadastro', icon: UserCog },
  { id: 'crise-grupo', label: 'Gestão de Crise', icon: AlertTriangle, children: [
    { id: 'crise', label: 'Painel de Crise', icon: Shield },
    { id: 'crise-negociacoes-agregadas', label: 'Negociações Agregadas', icon: Handshake },
    { id: 'crise-pagamentos', label: 'Pagamentos', icon: DollarSign },
    { id: 'crise-encargos', label: 'Encargos e Juros', icon: Percent },
    { id: 'crise-parcelamentos', label: 'Parcelamentos', icon: CalendarCheck },
    { id: 'crise-anuidades', label: 'Anuidades', icon: CreditCard },
  ]},
  { id: 'chamados', label: 'Chamados', icon: LifeBuoy },
  { id: 'admin-grupo', label: 'Administração', icon: Settings, children: [
    { id: 'usuarios', label: 'Usuários', icon: UserCog },
  ]},
];

// Verifica se o usuário tem acesso a um item de menu (ou a algum filho)
function temAcessoItem(item, paginasPermitidas) {
  if (!paginasPermitidas) return false;
  if (paginasPermitidas === '*') return true;
  // Item folha: verificar se a página está na lista
  if (!item.children || item.children.length === 0) {
    return paginasPermitidas.includes(item.id);
  }
  // Item com filhos: mostrar se tem acesso a pelo menos um filho
  return item.children.some(child => temAcessoItem(child, paginasPermitidas));
}

function MenuItem({ item, level = 0, activePage, onNavigate, expandedSections, toggleSection, paginasPermitidas }) {
  // Não renderizar se não tem acesso
  if (!temAcessoItem(item, paginasPermitidas)) return null;

  const isActive = activePage === item.id;
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedSections[item.id];
  const Icon = item.icon;
  const paddingLeft = 16 + level * 16;

  if (hasChildren) {
    // Filtrar filhos visíveis
    const filhosVisiveis = item.children.filter(child => temAcessoItem(child, paginasPermitidas));
    if (filhosVisiveis.length === 0) return null;

    return (
      <div>
        <button
          onClick={() => toggleSection(item.id)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: `10px 12px 10px ${paddingLeft}px`, border: 'none', cursor: 'pointer',
            background: isExpanded ? `${colors.primary}08` : 'transparent',
            color: isExpanded ? colors.text : colors.textMuted,
            fontSize: 13, fontWeight: isExpanded ? 600 : 500, textAlign: 'left',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = `${colors.primary}10`; }}
          onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
        >
          {Icon && <Icon size={16} />}
          <span style={{ flex: 1 }}>{item.label}</span>
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {isExpanded && (
          <div>
            {filhosVisiveis.map(child => (
              <MenuItem
                key={child.id} item={child} level={level + 1}
                activePage={activePage} onNavigate={onNavigate}
                expandedSections={expandedSections} toggleSection={toggleSection}
                paginasPermitidas={paginasPermitidas}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => !item.disabled && onNavigate(item.id)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: `9px 12px 9px ${paddingLeft}px`, border: 'none',
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        background: isActive ? `${colors.primary}20` : 'transparent',
        color: item.disabled ? colors.textDim : isActive ? colors.primaryLight : colors.textMuted,
        fontSize: 13, fontWeight: isActive ? 600 : 400, textAlign: 'left',
        borderLeft: isActive ? `3px solid ${colors.primary}` : '3px solid transparent',
        transition: 'all 0.15s',
        opacity: item.disabled ? 0.5 : 1,
      }}
      onMouseEnter={e => { if (!isActive && !item.disabled) e.currentTarget.style.background = `${colors.primary}10`; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
    >
      {Icon && <Icon size={15} />}
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge && (
        <span style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 4,
          background: `${colors.warning}22`, color: colors.warning, fontWeight: 600
        }}>{item.badge}</span>
      )}
    </button>
  );
}

export default function Layout({ activePage, onNavigate, children, usuario, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ financeiro: true, cadastro: true, movimentacao: true, conciliacoes: true });

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Celular (03/11/2026): a barra lateral fixa comia 240 dos 375px da tela.
  // Lá ela vira menu de gaveta, aberto pelo ☰ da barra do topo, e fecha ao
  // escolher a página.
  const mobile = useIsMobile();
  const [menuAberto, setMenuAberto] = useState(false);
  const recolhido = !mobile && collapsed;
  const navegar = (id) => { onNavigate(id); setMenuAberto(false); };

  const sidebarWidth = mobile ? 0 : (collapsed ? 60 : 240);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: colors.bg, color: colors.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {mobile && menuAberto && (
        <div onClick={() => setMenuAberto(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 290 }} />
      )}
      {/* Sidebar */}
      <aside style={{
        width: mobile ? 'min(280px, 85vw)' : sidebarWidth, background: colors.sidebar, borderRight: `1px solid ${colors.border}`,
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: mobile ? 300 : 200,
        display: 'flex', flexDirection: 'column',
        transition: mobile ? 'transform 0.2s' : 'width 0.2s',
        transform: mobile && !menuAberto ? 'translateX(-100%)' : 'none',
        boxShadow: mobile && menuAberto ? '0 0 40px rgba(0,0,0,0.5)' : 'none',
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{
          padding: '16px 12px', borderBottom: `1px solid ${colors.border}`,
          display: 'flex', alignItems: 'center', justifyContent: recolhido ? 'center' : 'space-between',
          minHeight: 56,
        }}>
          {!recolhido && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: '#fff',
              }}>XM</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, lineHeight: 1.2 }}>Controle</div>
                <div style={{ fontSize: 10, color: colors.textDim, fontWeight: 500 }}>Gestão Financeira</div>
              </div>
            </div>
          )}
          <button
            onClick={() => (mobile ? setMenuAberto(false) : setCollapsed(!collapsed))}
            style={{
              background: 'transparent', border: 'none', color: colors.textMuted,
              cursor: 'pointer', padding: mobile ? 8 : 4, borderRadius: 4,
            }}
          >
            {recolhido ? <Menu size={18} /> : <X size={mobile ? 20 : 16} />}
          </button>
        </div>

        {/* Navigation */}
        {!recolhido && (
          <nav style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
            {menuItems.map(item => (
              <MenuItem
                key={item.id} item={item}
                activePage={activePage} onNavigate={navegar}
                expandedSections={expandedSections} toggleSection={toggleSection}
                paginasPermitidas={usuario?.paginas_permitidas}
              />
            ))}
          </nav>
        )}

        {/* Footer - User info */}
        {!recolhido && (
          <div style={{ borderTop: `1px solid ${colors.border}`, padding: '12px 14px' }}>
            {usuario && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 11, fontWeight: 700,
                }}>{(usuario.nome || 'U')[0].toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {usuario.nome} {usuario.sobrenome || ''}
                  </div>
                  <div style={{ fontSize: 10, color: colors.textDim }}>{usuario.login}</div>
                </div>
                <button onClick={estaEmVerComo() ? sairDoVerComo : onLogout} title={estaEmVerComo() ? 'Fechar o ver como' : 'Sair'} style={{
                  background: 'transparent', border: 'none', color: colors.textDim,
                  cursor: 'pointer', padding: 4, borderRadius: 4, flexShrink: 0,
                }}
                  onMouseEnter={e => e.currentTarget.style.color = colors.danger}
                  onMouseLeave={e => e.currentTarget.style.color = colors.textDim}
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: colors.textDim }}>
              <Database size={10} />
              <span>PostgreSQL</span>
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, marginLeft: sidebarWidth, transition: 'margin-left 0.2s', minWidth: 0, maxWidth: `calc(100vw - ${sidebarWidth}px)`, overflow: 'hidden' }}>
        {mobile && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 150, display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px', minHeight: 52, background: colors.sidebar, borderBottom: `1px solid ${colors.border}`,
          }}>
            <button onClick={() => setMenuAberto(true)} aria-label="Abrir menu" style={{
              background: 'transparent', border: 'none', color: colors.text, cursor: 'pointer', padding: 6, marginLeft: -6,
              display: 'flex', alignItems: 'center',
            }}>
              <Menu size={22} />
            </button>
            <div style={{
              width: 28, height: 28, borderRadius: 7, background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12, color: '#fff', flexShrink: 0,
            }}>XM</div>
            <div style={{ fontSize: 15, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getPageTitle(activePage)}
            </div>
          </div>
        )}
        {/* Banner: impersonação (admin "Vendo como") tem prioridade sobre login Titular */}
        {(() => {
          // "Ver como" de um usuário, numa aba à parte (03/11/2026).
          if (estaEmVerComo()) {
            return (
              <div style={{
                padding: mobile ? '8px 12px' : '10px 24px', background: '#f97316', color: '#000', flexWrap: 'wrap',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13, fontWeight: 600,
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 16 }}>🎭</span>
                  <span>
                    Vendo como <b>{`${usuario?.nome || ''} ${usuario?.sobrenome || ''}`.trim() || usuario?.login}</b>
                    {' '}({usuario?.login}{(usuario?.perfis || []).length ? ` · ${usuario.perfis.join(', ')}` : ''})
                    {' '}— o que você fizer nesta aba fica registrado como ele.
                  </span>
                </div>
                <button onClick={sairDoVerComo} style={{
                  background: '#000', color: '#f97316', border: 'none', borderRadius: 6,
                  padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                }}>
                  Fechar esta aba
                </button>
              </div>
            );
          }
          const impersonando = typeof window !== 'undefined' && localStorage.getItem('xm_token_admin');
          if (impersonando) {
            const sairImpersonacao = () => {
              const tk = localStorage.getItem('xm_token_admin');
              const us = localStorage.getItem('xm_usuario_admin');
              if (tk && us) {
                localStorage.setItem('xm_token', tk);
                localStorage.setItem('xm_usuario', us);
              }
              localStorage.removeItem('xm_token_admin');
              localStorage.removeItem('xm_usuario_admin');
              window.location.reload();
            };
            return (
              <div style={{
                padding: mobile ? '8px 12px' : '10px 24px', background: '#f97316', color: '#000', flexWrap: 'wrap',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13, fontWeight: 600,
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🎭</span>
                  <span>Você está visualizando como <b>{usuario?.titular_nome || usuario?.nome}</b> (Titular) — apenas leitura recomendada</span>
                </div>
                <button onClick={sairImpersonacao} style={{
                  background: '#000', color: '#f97316', border: 'none', borderRadius: 6,
                  padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                }}>
                  ← Voltar ao admin
                </button>
              </div>
            );
          }
          // Banner padrão para usuário Titular real (login direto)
          if (usuario?.titular_id && usuario?.titular_nome) {
            return (
              <div style={{
                padding: mobile ? '8px 12px' : '8px 24px', background: `${colors.info}15`, borderBottom: `1px solid ${colors.info}30`,
                display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.info,
              }}>
                <Users size={14} />
                <span>Visualizando dados de: <b>{usuario.titular_nome}</b></span>
              </div>
            );
          }
          return null;
        })()}
        {/* Page content */}
        <div style={{ padding: mobile ? 12 : 24, minWidth: 0, overflow: 'hidden' }}>
          {children}
        </div>
      </main>
    </div>
  );
}

// Título da barra do topo no celular: o rótulo da página no próprio menu.
function getPageTitle(page, itens = menuItems) {
  for (const item of itens) {
    if (item.id === page && !item.children) return item.label;
    if (item.children) {
      const achou = getPageTitle(page, item.children);
      if (achou) return achou;
    }
  }
  return itens === menuItems ? (page === 'chamado-detalhe' ? 'Chamado' : 'Controle') : null;
}
